import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ColumnDef } from '@tanstack/react-table';
import { Ban, FileText, Mail, MoreHorizontal, Pencil, Plus, Trash2, Upload, Download } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import apiService from '../services/api.service';
import TeamsPdfService from '../services/teams-pdf.service';
import { exportTable } from '../lib/tableExport';
import { estInscriptionAnnulee } from '../lib/registrationStatus';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { StatusDot, StatusTone } from '../components/ui/status-dot';
import { PageHeader, Toolbar } from '../components/ui/page-header';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { ExportMenu } from '../components/ExportMenu';
import { DataTable } from '../components/data-table/DataTable';
import { BulkAction, BulkActionBar } from '../components/data-table/BulkActionBar';
import { FilterChip } from '../components/data-table/FilterChip';
import { SearchField } from '../components/data-table/SearchField';
import { TeamSheet, TeamFormValues, TeamPatch, participantsPayload } from '../components/teams/TeamSheet';
import { CategoryChangeDialog, CategoryChangePreview } from '../components/teams/CategoryChangeDialog';
import { CancelRegistrationDialog } from '../components/teams/CancelRegistrationDialog';
import { ImportTeamsDialog } from '../components/teams/ImportTeamsDialog';
import { TeamRow, teamsQueryKey, useCategories, useTeams } from '../hooks/useTeams';
import { overviewKey } from '../hooks/useEventOverview';

// Équipes (refonte 2026-09-23) : un tableau de travail.
//  - l'état se lit en point + libellé, la catégorie en texte (plus d'aplats) ;
//  - une colonne « Heat » dit tout de suite qui n'est placé nulle part ;
//  - filtres en étiquettes retirables, recherche toujours visible ;
//  - sélection multiple → barre d'actions (écrire, exporter, supprimer) ;
//  - édition dans un panneau latéral, la liste restant visible.

type PaymentKey = 'paid' | 'pending' | 'cancelled' | 'manual';
type HeatKey = 'placed' | 'unplaced';

const paymentOf = (team: TeamRow): PaymentKey => {
  if (!team.registration) return 'manual';
  if (estInscriptionAnnulee(team.registration)) return 'cancelled';
  return team.registration.status === 'confirmed' ? 'paid' : 'pending';
};

const PAYMENT_TONE: Record<PaymentKey, StatusTone> = {
  paid: 'success',
  pending: 'warning',
  cancelled: 'destructive',
  manual: 'neutral',
};

const heatLabel = (h: TeamRow['heats'][number], t: (k: string, o?: any) => string) =>
  h.wod_number != null
    ? t('teams.heatShort', { wod: h.wod_number, heat: h.heat_number })
    : t('teams.heatOnly', { heat: h.heat_number });

const Teams: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedEventId, selectedEvent } = useEvent();

  const teamsQuery = useTeams(selectedEventId);
  const categoriesQuery = useCategories(selectedEventId);
  const teams = useMemo(() => teamsQuery.data ?? [], [teamsQuery.data]);
  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data]);
  const categoryName = useCallback(
    (id?: string) => (id ? categories.find((c) => c.id === id)?.name ?? t('teams.unknownCategory') : t('teams.noCategory')),
    [categories, t],
  );

  // Filtres
  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState<string[]>([]);
  const [payFilter, setPayFilter] = useState<string[]>([]);
  const [heatFilter, setHeatFilter] = useState<string[]>([]);
  const eventHasHeats = teams.some((tm) => tm.heats.length > 0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return teams.filter((tm) => {
      if (catFilter.length && !catFilter.includes(tm.category_id || '')) return false;
      if (payFilter.length && !payFilter.includes(paymentOf(tm))) return false;
      if (heatFilter.length && !heatFilter.includes(tm.heats.length ? 'placed' : 'unplaced')) return false;
      if (!q) return true;
      return [tm.team_id, tm.name, tm.contact_email, categoryName(tm.category_id), tm.participantNames,
        ...tm.participantList.map((p) => p.email)]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [teams, query, catFilter, payFilter, heatFilter, categoryName]);

  // Sélection
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const selectedIds = Object.keys(selection).filter((id) => selection[id]);
  const selectedTeams = teams.filter((tm) => selection[tm.id]);
  useEffect(() => { setSelection({}); }, [selectedEventId]);

  // Panneau d'édition — l'URL porte l'équipe ouverte (?team=…), donc la
  // palette ⌘K, la vue d'ensemble et la notification « nouvel inscrit » y
  // mènent directement. Un lien vers une équipe OUVRE l'équipe : jusqu'au
  // 2026-09-27 il fallait un `edit=1` en plus, que seule la palette posait,
  // et la notification ne faisait que surligner la ligne (constat de Bryan).
  const [sheetTeamId, setSheetTeamId] = useState<string | null>(null);
  const [sheetCreate, setSheetCreate] = useState(false);
  const [sheetDirty, setSheetDirty] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);
  // Arrivée par un lien vers une équipe : un seul flash, ~1,5 s.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const sheetTeam = teams.find((tm) => tm.id === sheetTeamId) ?? null;
  const sheetOpen = sheetCreate || !!sheetTeam;

  useEffect(() => {
    const team = searchParams.get('team');
    const create = searchParams.get('create');
    if (create === 'true') setSheetCreate(true);
    if (team) setSheetTeamId(team);
    if (team || create) {
      // Mise en évidence de la ligne (arrivée depuis un lien) puis nettoyage.
      if (team) {
        setHighlightId(team);
        setTimeout(() => document.getElementById(`team-${team}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
        setTimeout(() => setHighlightId(null), 1500);
      }
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('team'), searchParams.get('create')]);

  const openTeam = (id: string) => {
    if (sheetOpen && sheetDirty && id !== sheetTeamId) { setPendingSwitch(id); return; }
    setSheetCreate(false);
    setSheetTeamId(id);
  };
  const closeSheet = () => {
    setSheetTeamId(null);
    setSheetCreate(false);
    setServerError('');
    if (searchParams.get('team')) { searchParams.delete('team'); setSearchParams(searchParams, { replace: true }); }
  };
  const indexInView = filtered.findIndex((tm) => tm.id === sheetTeamId);

  // Écriture
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const [catChange, setCatChange] = useState<{ preview: CategoryChangePreview; teamId: string; newCategoryId: string } | null>(null);
  const [catChangeLoading, setCatChangeLoading] = useState(false);
  const [catChangeError, setCatChangeError] = useState('');

  const refresh = () => {
    qc.invalidateQueries({ queryKey: teamsQueryKey(selectedEventId) });
    qc.invalidateQueries({ queryKey: overviewKey(selectedEventId) });
  };

  // Création : le seul chemin qui garde un bouton (pas de ligne en base avant).
  const submit = async (values: TeamFormValues) => {
    if (!selectedEventId) return;
    setSaving(true);
    setServerError('');
    const payload = {
      team_name: values.name.trim(),
      participants: participantsPayload(values.participants),
      contact_email: values.contact_email.trim(),
      contact_phone: values.contact_phone,
      category_id: values.category_id,
      team_id: values.team_id,
      event_id: selectedEventId,
    };
    try {
      await apiService.createTeam(payload);
      toast.success(t('teams.form.created', { name: payload.team_name }));
      setSheetDirty(false);
      closeSheet();
      refresh();
    } catch (err: any) {
      setServerError(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // Édition : chaque champ s'écrit dès qu'il a changé (sauvegarde automatique,
  // règle du 22/09). `PUT /teams` est partiel : on n'envoie que ce qui a bougé.
  const autoSaveTeam = async (patch: TeamPatch) => {
    if (!sheetTeam) return;
    await apiService.updateTeam(sheetTeam.id, patch);
    refresh();
  };

  // Catégorie : LOG-101 — une équipe qui a PAYÉ change par le flux officiel
  // (fenêtre d'impact, complément ou remboursement), jamais par PUT /teams
  // (le serveur le refuse, LOG-103). Une équipe non payée change tout de
  // suite, avec « Annuler » sur le message.
  const changeCategory = async (newCategoryId: string): Promise<boolean> => {
    if (!sheetTeam || !selectedEventId) return false;
    const team = sheetTeam;
    const before = team.category_id || '';
    try {
      if (newCategoryId) {
        const preview = await apiService.getCategoryChangePreview(team.id, newCategoryId);
        if (preview.has_paid_registration) {
          setCatChange({ preview, teamId: team.id, newCategoryId });
          return false;
        }
      }
      await apiService.updateTeam(team.id, { category_id: newCategoryId });
      refresh();
      const name = categories.find((c) => c.id === newCategoryId)?.name ?? t('teams.noCategory');
      toast.success(t('teams.form.categoryChanged', { name }), {
        action: {
          label: t('common.undo'),
          onClick: () => {
            apiService.updateTeam(team.id, { category_id: before })
              .then(refresh)
              .catch((err: any) => toast.error(err.response?.data?.detail || t('errors.generic')));
          },
        },
      });
      return true;
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
      return false;
    }
  };

  const confirmCategoryChange = async () => {
    if (!catChange || !selectedEventId) return;
    setCatChangeLoading(true);
    setCatChangeError('');
    try {
      const result = await apiService.requestCategoryChange({
        team_id: catChange.teamId,
        new_category_id: catChange.newCategoryId,
        event_id: selectedEventId,
      });
      toast.success(result.checkout_url
        ? t('teams.categoryChange.paymentLinkSent', { email: catChange.preview.contact_email || '' })
        : t('teams.categoryChange.changeSuccess'));
      setCatChange(null);
      refresh();
    } catch (err: any) {
      setCatChangeError(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setCatChangeLoading(false);
    }
  };

  // Suppression — irréversible côté serveur : elle se confirme.
  const [toDelete, setToDelete] = useState<TeamRow[]>([]);
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    setDeleting(true);
    const failed: string[] = [];
    for (const tm of toDelete) {
      try { await apiService.deleteTeam(tm.id); } catch { failed.push(tm.name); }
    }
    setDeleting(false);
    setToDelete([]);
    setSelection({});
    if (toDelete.some((tm) => tm.id === sheetTeamId)) closeSheet();
    refresh();
    // Un échec partiel se dit en nommant les équipes restées, jamais en silence.
    if (failed.length) toast.error(t('teams.deleteFailed', { names: failed.join(', ') }));
    else toast.success(t('teams.deleted', { count: toDelete.length }));
  };

  const [cancelTeam, setCancelTeam] = useState<TeamRow | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  // Export
  const exportData = (rows: TeamRow[]) => ({
    filename: `equipes-${selectedEvent?.name ?? ''}`,
    title: t('nav.teams'),
    subtitle: selectedEvent?.name,
    headers: [t('teams.teamId'), t('teams.teamName'), t('teams.participants'), t('teams.contact'), t('teams.category'), t('teams.col.payment'), t('teams.col.heat')],
    rows: rows.map((tm) => [
      tm.team_id || '',
      tm.name,
      tm.participantNames,
      tm.contact_email || '',
      categoryName(tm.category_id),
      t(`teams.payment.${paymentOf(tm)}`),
      tm.heats.map((h) => heatLabel(h, t)).join(', '),
    ]),
  });

  const exportPdf = async () => {
    if (!selectedEvent) return;
    await TeamsPdfService.exportTeamsList({
      eventName: selectedEvent.name,
      organizationName: undefined,
      teams: teams.map((tm) => ({
        id: tm.id,
        name: tm.name,
        category_name: categories.find((c) => c.id === tm.category_id)?.name,
        team_id: tm.team_id,
        members: tm.participantList,
      })),
    });
  };

  const columns = useMemo<ColumnDef<TeamRow, any>[]>(() => [
    {
      id: 'team_id',
      accessorFn: (tm) => tm.team_id || '',
      header: () => t('teams.col.number'),
      sortingFn: (a, b) => (a.original.team_id || '').localeCompare(b.original.team_id || '', undefined, { numeric: true }),
      cell: ({ row }) => <span className="tabular-nums text-muted-foreground">{row.original.team_id}</span>,
    },
    {
      id: 'name',
      accessorFn: (tm) => tm.name,
      header: () => t('teams.teamName'),
      sortingFn: (a, b) => a.original.name.localeCompare(b.original.name, undefined, { sensitivity: 'base' }),
      cell: ({ row }) => {
        const tm = row.original;
        const cancelled = paymentOf(tm) === 'cancelled';
        return (
          <div className="min-w-0">
            <div className={cn('font-semibold text-foreground', cancelled && 'line-through opacity-50')}>{tm.name}</div>
            {tm.participantNames && <div className="mt-0.5 truncate text-xs text-muted-foreground">{tm.participantNames}</div>}
          </div>
        );
      },
    },
    {
      id: 'category',
      accessorFn: (tm) => categoryName(tm.category_id),
      header: () => t('teams.category'),
      cell: ({ getValue }) => <span className="text-muted-foreground">{getValue()}</span>,
    },
    {
      id: 'payment',
      accessorFn: (tm) => paymentOf(tm),
      header: () => t('teams.col.payment'),
      cell: ({ row }) => {
        const k = paymentOf(row.original);
        return <StatusDot tone={PAYMENT_TONE[k]} label={t(`teams.payment.${k}`)} className="text-muted-foreground" />;
      },
    },
    {
      id: 'heat',
      accessorFn: (tm) => tm.heats.length,
      header: () => t('teams.col.heat'),
      cell: ({ row }) => {
        const hs = row.original.heats;
        if (!hs.length) {
          return eventHasHeats && paymentOf(row.original) !== 'cancelled'
            ? <span className="text-sm text-warning">{t('teams.unplaced')}</span>
            : <span className="text-muted-foreground">—</span>;
        }
        // Une heat par WOD : chaque placement sur sa propre ligne courte, jamais
        // coupé au milieu (« WOD 2 · H1 » ne doit pas se lire « WOD 2 · » / « H1 »).
        return (
          <span className="flex flex-col text-sm leading-5 text-muted-foreground">
            {hs.map((h) => <span key={h.heat_id} className="whitespace-nowrap">{heatLabel(h, t)}</span>)}
          </span>
        );
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => {
        const tm = row.original;
        const cancelled = paymentOf(tm) === 'cancelled';
        return (
          <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={t('teams.rowActions', { name: tm.name })}>
                  <MoreHorizontal size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {!cancelled && (
                  <DropdownMenuItem onClick={() => openTeam(tm.id)}><Pencil size={16} className="mr-2" />{t('common.edit')}</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate(`/admin/messages?teams=${tm.id}`)}><Mail size={16} className="mr-2" />{t('teams.sendMessage')}</DropdownMenuItem>
                {tm.registration && !cancelled && (
                  <DropdownMenuItem onClick={() => setCancelTeam(tm)}><Ban size={16} className="mr-2" />{t('teams.cancelRegistration.action')}</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setToDelete([tm])}>
                  <Trash2 size={16} className="mr-2" />{t('common.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, categoryName, eventHasHeats, navigate]);

  if (!selectedEventId) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader title={t('nav.teams')} />
        <p className="mt-8 text-muted-foreground">{t('teams.selectEventToManage')}</p>
      </div>
    );
  }

  const unplacedCount = eventHasHeats ? teams.filter((tm) => !tm.heats.length && paymentOf(tm) !== 'cancelled').length : 0;
  const count = (key: string, pred: (tm: TeamRow) => boolean) => ({ key, count: teams.filter(pred).length });

  return (
    <div className={cn('space-y-5 p-4 transition-[padding] duration-200 md:p-8', sheetOpen && 'min-[1600px]:pr-[584px]')}>
      <PageHeader
        eyebrow={selectedEvent?.name}
        title={t('nav.teams')}
        meta={teamsQuery.isLoading ? null : [
          t('teams.count', { count: teams.length }),
          unplacedCount ? t('teams.unplacedCount', { count: unplacedCount }) : null,
        ].filter(Boolean).join(' · ')}
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => setImportOpen(true)}><Upload size={16} />{t('common.import')}</Button>
            <ExportMenu getData={() => exportData(filtered)} disabled={!teams.length} />
            <Button variant="outline" size="sm" onClick={exportPdf} disabled={!teams.length} title={t('teams.pdfList')}>
              <FileText size={16} />PDF
            </Button>
            <Button size="sm" onClick={() => { setSheetTeamId(null); setSheetCreate(true); }}>
              <Plus size={16} />{t('teams.newTeam')}
            </Button>
          </>
        }
      />

      <Toolbar>
        <SearchField value={query} onChange={setQuery} placeholder={t('teams.filters.searchPlaceholder')} />
        {categories.length > 0 && (
          <FilterChip
            label={t('teams.category')}
            selected={catFilter}
            onChange={setCatFilter}
            options={categories.map((c) => ({ value: c.id, label: c.name, count: teams.filter((tm) => tm.category_id === c.id).length }))}
          />
        )}
        <FilterChip
          label={t('teams.col.payment')}
          selected={payFilter}
          onChange={setPayFilter}
          options={(['paid', 'pending', 'cancelled', 'manual'] as PaymentKey[])
            .map((k) => ({ ...count(k, (tm) => paymentOf(tm) === k), value: k, label: t(`teams.payment.${k}`) }))
            .filter((o) => o.count > 0)}
        />
        {eventHasHeats && (
          <FilterChip
            label={t('teams.col.heat')}
            selected={heatFilter}
            onChange={setHeatFilter}
            options={(['placed', 'unplaced'] as HeatKey[]).map((k) => ({
              value: k,
              label: t(`teams.heatFilter.${k}`),
              count: teams.filter((tm) => (tm.heats.length ? 'placed' : 'unplaced') === k).length,
            }))}
          />
        )}
        {(catFilter.length + payFilter.length + heatFilter.length > 0 || query) && (
          <span className="text-xs text-muted-foreground">{t('teams.filteredCount', { shown: filtered.length, total: teams.length })}</span>
        )}
      </Toolbar>

      {teamsQuery.isError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-4">
            {t('teams.loadError')}
            <Button variant="outline" size="sm" onClick={() => teamsQuery.refetch()}>{t('common.retry')}</Button>
          </AlertDescription>
        </Alert>
      )}

      <DataTable
        ariaLabel={t('nav.teams')}
        columns={columns}
        data={filtered}
        getRowId={(tm) => tm.id}
        loading={teamsQuery.isLoading}
        selectable
        rowSelection={selection}
        onRowSelectionChange={setSelection}
        onRowClick={(tm) => { if (paymentOf(tm) !== 'cancelled') openTeam(tm.id); }}
        activeRowId={sheetTeamId}
        rowDomId={(tm) => `team-${tm.id}`}
        rowClassName={(tm) => (tm.id === highlightId ? 'bg-white/[0.08] transition-colors duration-700' : undefined)}
        initialSorting={[{ id: 'team_id', desc: false }]}
        empty={teams.length === 0 ? (
          <div className="space-y-4">
            <p className="text-base text-foreground">{t('teams.noTeams')}</p>
            <p>{t('teams.emptyHint')}</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={() => setImportOpen(true)}><Upload size={16} />{t('teams.importFromExcel')}</Button>
              <Button onClick={() => setSheetCreate(true)}><Plus size={16} />{t('teams.addTeam')}</Button>
            </div>
          </div>
        ) : t('teams.noMatch')}
        renderCard={(tm, { selected, toggle }) => (
          <div className="flex items-start gap-3 py-3">
            <input
              type="checkbox"
              checked={selected}
              onChange={toggle}
              onClick={(e) => e.stopPropagation()}
              aria-label={t('dataTable.selectRow')}
              className="mt-1 h-4 w-4 accent-white"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="tabular-nums text-xs text-muted-foreground">{tm.team_id}</span>
                <span className={cn('truncate font-semibold', paymentOf(tm) === 'cancelled' && 'line-through opacity-50')}>{tm.name}</span>
              </div>
              {tm.participantNames && <div className="truncate text-xs text-muted-foreground">{tm.participantNames}</div>}
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>{categoryName(tm.category_id)}</span>
                <StatusDot tone={PAYMENT_TONE[paymentOf(tm)]} label={t(`teams.payment.${paymentOf(tm)}`)} />
                {eventHasHeats && (tm.heats.length
                  ? <span>{tm.heats.map((h) => heatLabel(h, t)).join(', ')}</span>
                  : <span className="text-warning">{t('teams.unplaced')}</span>)}
              </div>
            </div>
          </div>
        )}
      />

      <BulkActionBar count={selectedIds.length} onClear={() => setSelection({})}>
        <BulkAction onClick={() => navigate(`/admin/messages?teams=${selectedIds.join(',')}`)}><Mail />{t('teams.sendMessage')}</BulkAction>
        <BulkAction onClick={() => exportTable('xlsx', exportData(selectedTeams)).catch(() => toast.error(t('export.failed')))}>
          <Download />{t('teams.exportSelection')}
        </BulkAction>
        {unplacedCount > 0 && (
          <BulkAction onClick={() => navigate('/admin/heats')}>{t('teams.openPlanning')}</BulkAction>
        )}
        <BulkAction className="text-destructive" onClick={() => setToDelete(selectedTeams)}><Trash2 />{t('common.delete')}</BulkAction>
      </BulkActionBar>

      <TeamSheet
        open={sheetOpen}
        team={sheetCreate ? null : sheetTeam}
        categories={categories}
        takenCodes={teams.filter((tm) => tm.id !== sheetTeamId).map((tm) => tm.team_id).filter(Boolean)}
        saving={saving}
        serverError={serverError}
        heatSummary={sheetTeam && eventHasHeats
          ? (sheetTeam.heats.length ? sheetTeam.heats.map((h) => heatLabel(h, t)).join(', ') : t('teams.unplaced'))
          : undefined}
        onSubmit={submit}
        onAutoSave={autoSaveTeam}
        onCategoryChange={changeCategory}
        onClose={closeSheet}
        onDirtyChange={setSheetDirty}
        onPrev={indexInView > 0 ? () => setSheetTeamId(filtered[indexInView - 1].id) : undefined}
        onNext={indexInView >= 0 && indexInView < filtered.length - 1 ? () => setSheetTeamId(filtered[indexInView + 1].id) : undefined}
      />

      <ConfirmDialog
        open={pendingSwitch !== null}
        onOpenChange={(o) => { if (!o) setPendingSwitch(null); }}
        title={t('teams.form.discardTitle')}
        message={t('teams.form.discardMessage')}
        confirmLabel={t('teams.form.discardConfirm')}
        destructive
        onConfirm={() => { setSheetDirty(false); setSheetTeamId(pendingSwitch); setPendingSwitch(null); }}
      />

      <ConfirmDialog
        open={toDelete.length > 0}
        onOpenChange={(o) => { if (!o && !deleting) setToDelete([]); }}
        title={t('common.confirmDeleteTitle')}
        message={toDelete.length === 1
          ? t('teams.deleteConfirm', { name: toDelete[0].name })
          : t('teams.deleteManyConfirm', { count: toDelete.length })}
        description={t('teams.deleteIrreversible')}
        confirmLabel={t('common.delete')}
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
      />

      <CategoryChangeDialog
        preview={catChange?.preview ?? null}
        loading={catChangeLoading}
        error={catChangeError}
        onConfirm={confirmCategoryChange}
        onCancel={() => { setCatChange(null); setCatChangeError(''); }}
      />

      <CancelRegistrationDialog team={cancelTeam} onClose={() => setCancelTeam(null)} onDone={refresh} />
      <ImportTeamsDialog
        open={importOpen}
        eventId={selectedEventId}
        onOpenChange={setImportOpen}
        onImported={() => { refresh(); qc.invalidateQueries({ queryKey: ['categories', selectedEventId] }); }}
      />
    </div>
  );
};

export default Teams;
